/** biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: This is a complex provider */

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import type {
  ProcessedTool,
  ProcessedToolkit,
  ToolkitCollection,
} from "@/clients/arcade/arcade";
import type { SelectionMode } from "../index";
import { isStarterToolkit } from "../utils/toolkit-grouping";

export type ToolkitPickerInitialProps = {
  selectedToolkits?: string[];
  selectedTools?: string[];
  committedToolkits?: string[];
  committedTools?: string[];
};

export type UseToolkitPickerProps = {
  toolkitCollection?: ToolkitCollection;
  selectionMode?: SelectionMode;
  initialProps?: ToolkitPickerInitialProps;
  onSearchEmpty?: (query: string) => void;
};

export type ToolkitPickerTab = "toolkits" | "tools";
export type ToolkitSelectionMap = Map<string, ProcessedToolkit>;
export type ToolSelectionMap = Map<string, boolean>;

// Internal reducer state (without derived values)
type ReducerState = {
  selectionMode: SelectionMode;
  selectedToolkits: ToolkitSelectionMap;
  selectedTools: ToolSelectionMap;
  committedToolkits: ToolkitSelectionMap;
  committedTools: ToolSelectionMap;
  searchQuery: string;
  toolkitFilter: string;
  activeTab: ToolkitPickerTab;
  showUnoptimizedToolkits: boolean;
};

// Full state exposed to consumers (includes derived values)
type UseToolkitPickerState = ReducerState & {
  toolkitCollection?: ToolkitCollection;
  filteredToolkits: Set<ProcessedToolkit>;
  filteredTools: Set<ProcessedTool>;
  /** Whether there are any starter (unoptimized) toolkits in the collection */
  hasStarterToolkits: boolean;
  /** Count of starter toolkits hidden due to visibility rules (would match current search) */
  hiddenToolkitCount: number;
  /** Count of tools from starter toolkits hidden due to visibility rules (would match current search) */
  hiddenToolCount: number;
};

// Actions
type ToolkitPickerAction =
  | {
      type: "SET_INITIAL_SELECTIONS";
      payload: ToolkitPickerInitialProps;
      collection?: ToolkitCollection;
    }
  | { type: "TOGGLE_TOOLKIT"; payload: ProcessedToolkit }
  | {
      type: "TOGGLE_TOOL";
      payload: {
        toolFqn: string;
        toolkitName?: string;
        toolkit?: ProcessedToolkit;
        selectionMode: SelectionMode;
      };
    }
  | {
      type: "SELECT_ALL";
      payload: { visibleToolFqns?: string[]; collection?: ToolkitCollection };
    }
  | { type: "CLEAR_SELECTIONS" }
  | { type: "COMMIT_SELECTIONS" }
  | { type: "RESTORE_COMMITTED_STATE" }
  | { type: "SET_SEARCH_QUERY"; payload: string }
  | { type: "CLEAR_SEARCH_QUERY" }
  | { type: "SET_TOOLKIT_FILTER"; payload: string }
  | { type: "CLEAR_FILTER" }
  | { type: "SET_ACTIVE_TAB"; payload: ToolkitPickerTab }
  | { type: "TOGGLE_UNOPTIMIZED_TOOLKITS" };

// Utility functions
const WHITESPACE_REGEX = /\s+/;

const splitSearchTerms = (query: string): string[] =>
  query.split(WHITESPACE_REGEX).filter(Boolean);

const filterItems = <T extends { searchableText?: string }>(
  items: T[],
  query: string
): Set<T> => {
  if (!query) {
    return new Set(items);
  }
  const terms = splitSearchTerms(query);
  return new Set(
    items.filter((item) => {
      const text = item.searchableText?.toLowerCase() ?? "";
      return terms.every((term) => text.includes(term));
    })
  );
};

const getToolsFromToolkit = (
  name: string,
  collection?: ToolkitCollection
): ProcessedTool[] => {
  if (!collection) {
    return [];
  }
  const toolkit = collection.toolkitMap?.get(name);
  if (!toolkit?.tools?.length) {
    return [];
  }
  if (!collection.tools?.length) {
    return [];
  }
  const toolFqns = new Set(toolkit.tools.map((t) => t.fully_qualified_name));
  return collection.tools.filter((t) => toolFqns.has(t.fully_qualified_name));
};

const selectAllItems = (
  visibleToolFqns: string[] | undefined,
  toolkitCollection?: ToolkitCollection
): {
  selectedToolkits: ToolkitSelectionMap;
  selectedTools: ToolSelectionMap;
} => {
  if (!toolkitCollection) {
    return {
      selectedToolkits: new Map(),
      selectedTools: new Map(),
    };
  }

  const newToolkits = new Map<string, ProcessedToolkit>();
  const newTools = new Map<string, boolean>();

  if (visibleToolFqns) {
    for (const fqn of visibleToolFqns) {
      newTools.set(fqn, true);
      // Always include toolkit when any tool from it is selected
      const toolkitName = toolkitCollection.toolToToolkitMap?.get(fqn);
      if (toolkitName) {
        const toolkit = toolkitCollection.toolkitMap?.get(toolkitName);
        if (toolkit) {
          newToolkits.set(toolkitName, toolkit);
        }
      }
    }
  } else {
    for (const toolkit of toolkitCollection.toolkits) {
      newToolkits.set(toolkit.name, toolkit);
      for (const tool of toolkit.tools) {
        newTools.set(tool.fully_qualified_name, true);
      }
    }
  }
  return { selectedToolkits: newToolkits, selectedTools: newTools };
};

// Utility function to convert initial props string arrays to Maps
const processInitialSelections = (
  selectedToolkitNames: string[] | undefined,
  selectedToolNames: string[] | undefined,
  toolkitCollection?: ToolkitCollection
): {
  selectedToolkits: ToolkitSelectionMap;
  selectedTools: ToolSelectionMap;
} => {
  const selectedToolkits = new Map<string, ProcessedToolkit>();
  const selectedTools = new Map<string, boolean>();

  if (!toolkitCollection) {
    return { selectedToolkits, selectedTools };
  }

  // Convert toolkit names to ProcessedToolkit objects
  if (selectedToolkitNames) {
    for (const toolkitName of selectedToolkitNames) {
      const toolkit = toolkitCollection.toolkitMap?.get(toolkitName);
      if (toolkit) {
        selectedToolkits.set(toolkitName, toolkit);
      }
    }
  }

  // Convert tool FQNs to boolean flags
  if (selectedToolNames) {
    for (const toolFqn of selectedToolNames) {
      selectedTools.set(toolFqn, true);
    }
  }

  return { selectedToolkits, selectedTools };
};

// Create initial state
const createInitialState = (props: UseToolkitPickerProps): ReducerState => {
  const { selectedToolkits, selectedTools } = processInitialSelections(
    props.initialProps?.selectedToolkits,
    props.initialProps?.selectedTools,
    props.toolkitCollection
  );

  const { selectedToolkits: committedToolkits, selectedTools: committedTools } =
    processInitialSelections(
      props.initialProps?.committedToolkits ||
        props.initialProps?.selectedToolkits,
      props.initialProps?.committedTools || props.initialProps?.selectedTools,
      props.toolkitCollection
    );

  return {
    selectionMode: props.selectionMode || "multi",
    selectedToolkits,
    selectedTools,
    committedToolkits,
    committedTools,
    searchQuery: "",
    toolkitFilter: "",
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    activeTab: "toolkits" as ToolkitPickerTab,
    showUnoptimizedToolkits: false,
  };
};

// Reducer - simplified without filtered state management
const toolkitPickerReducer = (
  state: ReducerState,
  action: ToolkitPickerAction
): ReducerState => {
  switch (action.type) {
    case "TOGGLE_TOOLKIT": {
      const toolkit = action.payload;
      const { name, tools } = toolkit;
      const isSelected = state.selectedToolkits.has(name);
      const newToolkits = new Map(state.selectedToolkits);
      const newTools = new Map(state.selectedTools);

      if (isSelected) {
        newToolkits.delete(name);
        for (const tool of tools) {
          newTools.delete(tool.fully_qualified_name);
        }
      } else {
        newToolkits.set(name, toolkit);
        for (const tool of tools) {
          newTools.set(tool.fully_qualified_name, true);
        }
      }
      return {
        ...state,
        selectedToolkits: newToolkits,
        selectedTools: newTools,
      };
    }

    case "SET_INITIAL_SELECTIONS": {
      const initialProps = action.payload;
      const collection = action.collection;
      const { selectedToolkits, selectedTools } = processInitialSelections(
        initialProps.selectedToolkits,
        initialProps.selectedTools,
        collection
      );

      const {
        selectedToolkits: committedToolkits,
        selectedTools: committedTools,
      } = processInitialSelections(
        initialProps.committedToolkits || initialProps.selectedToolkits,
        initialProps.committedTools || initialProps.selectedTools,
        collection
      );

      return {
        ...state,
        selectedToolkits,
        selectedTools,
        committedToolkits,
        committedTools,
      };
    }

    case "TOGGLE_TOOL": {
      const { toolFqn, toolkitName, toolkit, selectionMode } = action.payload;
      const isSelected = state.selectedTools.has(toolFqn);
      const newToolkits = new Map(state.selectedToolkits);
      const newTools = new Map(state.selectedTools);

      if (isSelected) {
        newTools.delete(toolFqn);
        if (toolkitName && toolkit) {
          // Check if any other tools from this toolkit are selected
          const hasOther = toolkit.tools?.some(
            (t) =>
              t.fully_qualified_name !== toolFqn &&
              newTools.has(t.fully_qualified_name)
          );
          if (!hasOther) {
            newToolkits.delete(toolkitName);
          }
        }
      } else {
        // In single selection mode, clear all other tools first
        if (selectionMode === "single") {
          newTools.clear();
          newToolkits.clear();
        }

        newTools.set(toolFqn, true);
        if (toolkitName && toolkit) {
          newToolkits.set(toolkitName, toolkit);
        }
      }
      return {
        ...state,
        selectedTools: newTools,
        selectedToolkits: newToolkits,
      };
    }

    case "SELECT_ALL": {
      const { visibleToolFqns, collection } = action.payload;
      const hasNoItems = visibleToolFqns?.length === 0;
      const hasNoVisibleFqns = !visibleToolFqns;
      const hasNoToolkits = hasNoVisibleFqns && !collection?.toolkits?.length;
      if (hasNoItems || hasNoToolkits) {
        return state;
      }
      const updates = selectAllItems(visibleToolFqns, collection);
      return { ...state, ...updates };
    }

    case "CLEAR_SELECTIONS":
      return {
        ...state,
        selectedToolkits: new Map(),
        selectedTools: new Map(),
      };

    case "COMMIT_SELECTIONS":
      return {
        ...state,
        committedToolkits: new Map(state.selectedToolkits),
        committedTools: new Map(state.selectedTools),
      };

    case "RESTORE_COMMITTED_STATE":
      return {
        ...state,
        selectedToolkits: new Map(state.committedToolkits),
        selectedTools: new Map(state.committedTools),
      };

    case "SET_SEARCH_QUERY":
      if (!action.payload.trim()) {
        return { ...state, searchQuery: "", activeTab: "toolkits" };
      }
      return { ...state, searchQuery: action.payload };

    case "CLEAR_SEARCH_QUERY":
      return { ...state, searchQuery: "", activeTab: "toolkits" };

    case "SET_TOOLKIT_FILTER":
      return { ...state, toolkitFilter: action.payload, activeTab: "tools" };

    case "CLEAR_FILTER":
      return { ...state, toolkitFilter: "", activeTab: "toolkits" };

    case "SET_ACTIVE_TAB": {
      const newTab = action.payload;
      // Clear toolkit filter when switching to toolkits tab
      if (newTab === "toolkits" && state.toolkitFilter) {
        return { ...state, activeTab: newTab, toolkitFilter: "" };
      }
      return { ...state, activeTab: newTab };
    }

    case "TOGGLE_UNOPTIMIZED_TOOLKITS":
      return {
        ...state,
        showUnoptimizedToolkits: !state.showUnoptimizedToolkits,
      };

    default:
      return state;
  }
};

// Context and types
type ToolkitPickerContextType = {
  state: UseToolkitPickerState;
  toggleToolkit: (toolkit: ProcessedToolkit) => void;
  toggleTool: (tool: ProcessedTool) => void;
  selectAll: (visibleToolFqns?: string[]) => void;
  clearSelections: () => void;
  commitSelections: () => void;
  restoreCommittedState: () => void;
  setSearchQuery: (query: string) => void;
  clearSearchQuery: () => void;
  setToolkitFilter: (fqName: string) => void;
  clearFilter: () => void;
  setActiveTab: (tab: ToolkitPickerTab) => void;
  toggleUnoptimizedToolkits: () => void;
};

const ToolkitPickerContext = createContext<ToolkitPickerContextType | null>(
  null
);

// Provider
export function ToolkitPickerProvider({
  children,
  toolkitCollection,
  selectionMode,
  initialProps,
  onSearchEmpty,
}: { children: ReactNode } & UseToolkitPickerProps) {
  const props = {
    toolkitCollection,
    selectionMode,
    initialProps,
  };

  const [reducerState, dispatch] = useReducer(
    toolkitPickerReducer,
    props,
    createInitialState
  );

  // Derive filtered toolkits, tools, and effective tab from props + state
  const {
    searchQuery,
    toolkitFilter,
    activeTab,
    showUnoptimizedToolkits,
    selectedToolkits,
  } = reducerState;

  const {
    filteredToolkits,
    filteredTools,
    effectiveTab,
    hasStarterToolkits,
    hiddenToolkitCount,
    hiddenToolCount,
  } = useMemo(() => {
    const normalized = searchQuery.toLowerCase().trim();

    // Get base items from collection
    const allToolkits = toolkitCollection?.toolkits || [];
    const allTools = toolkitCollection?.tools || [];

    // Check if any starter toolkits exist (for showing toggle UI)
    const hasStarters = allToolkits.some(isStarterToolkit);

    // Pre-compute visible toolkit names based on visibility rules
    // null means all toolkits are visible (no filtering needed)
    const visibleToolkitNames = showUnoptimizedToolkits
      ? null
      : new Set(
          allToolkits
            .filter(
              (toolkit) =>
                !isStarterToolkit(toolkit) || selectedToolkits.has(toolkit.name)
            )
            .map((t) => t.name)
        );

    // Apply visibility filter for toolkits
    const visibleToolkits =
      visibleToolkitNames === null
        ? allToolkits
        : allToolkits.filter((t) => visibleToolkitNames.has(t.name));

    // Only filter by toolkit if it exists and has tools, otherwise show all tools
    const toolsFromToolkit = toolkitFilter
      ? getToolsFromToolkit(toolkitFilter, toolkitCollection)
      : [];
    // Bypass visibility filter when viewing a specific toolkit's tools (user explicitly chose to view it)
    const isViewingSpecificToolkit =
      toolkitFilter && toolsFromToolkit.length > 0;
    const baseTools = isViewingSpecificToolkit ? toolsFromToolkit : allTools;

    // Apply visibility filter for tools using pre-computed toolkit names
    // Skip visibility filter when an explicit toolkit filter is set - users should
    // see the tools they explicitly requested, regardless of the visibility toggle
    const visibleTools =
      visibleToolkitNames === null || isViewingSpecificToolkit
        ? baseTools
        : baseTools.filter((tool) => {
            const toolkitName = toolkitCollection?.toolToToolkitMap?.get(
              tool.fully_qualified_name
            );
            if (!toolkitName) {
              return true; // Tool has no toolkit association - show it
            }
            const toolkit = toolkitCollection?.toolkitMap?.get(toolkitName);
            if (!toolkit) {
              return true; // Toolkit name exists but toolkit object missing - show it
            }
            // Check if toolkit is visible
            return visibleToolkitNames.has(toolkitName);
          });

    // Apply search filter
    const toolkitsResult = filterItems(visibleToolkits, normalized);
    const toolsResult = filterItems(visibleTools, normalized);

    // Calculate hidden counts (starter items that would match search but are hidden)
    // When viewing a specific toolkit or when toggle is on, hidden count is 0
    let hiddenToolkits = 0;
    let hiddenTools = 0;

    if (!(showUnoptimizedToolkits || isViewingSpecificToolkit)) {
      // Count hidden starter toolkits that would match the search
      for (const tk of allToolkits) {
        if (isStarterToolkit(tk) && !selectedToolkits.has(tk.name)) {
          // Check if it would match the search
          const matchesSearch =
            !normalized ||
            (tk.searchableText?.toLowerCase().includes(normalized) ?? false);
          if (matchesSearch) {
            hiddenToolkits += 1;
          }
        }
      }

      // Count hidden tools from starter toolkits that would match the search
      const toolkitMap = toolkitCollection?.toolkitMap;
      const toolToToolkitMap = toolkitCollection?.toolToToolkitMap;
      for (const tool of allTools) {
        const toolkitName = toolToToolkitMap?.get(tool.fully_qualified_name);
        const toolkit = toolkitName ? toolkitMap?.get(toolkitName) : undefined;
        if (
          toolkit &&
          isStarterToolkit(toolkit) &&
          !selectedToolkits.has(toolkit.name)
        ) {
          // Check if it would match the search
          const searchTerms = splitSearchTerms(normalized);
          const matchesSearch =
            searchTerms.length === 0 ||
            searchTerms.every((term) =>
              (tool.searchableText?.toLowerCase() ?? "").includes(term)
            );
          if (matchesSearch) {
            hiddenTools += 1;
          }
        }
      }
    }

    // Auto-switch tabs based on search results
    let tabResult: ToolkitPickerTab = activeTab;
    if (normalized) {
      const hasToolkits = toolkitsResult.size > 0;
      const hasTools = toolsResult.size > 0;

      if (!hasToolkits && hasTools) {
        tabResult = "tools";
      } else if (hasToolkits && !hasTools) {
        tabResult = "toolkits";
      }
    }

    return {
      filteredToolkits: toolkitsResult,
      filteredTools: toolsResult,
      effectiveTab: tabResult,
      hasStarterToolkits: hasStarters,
      hiddenToolkitCount: hiddenToolkits,
      hiddenToolCount: hiddenTools,
    };
  }, [
    toolkitCollection,
    searchQuery,
    toolkitFilter,
    activeTab,
    showUnoptimizedToolkits,
    selectedToolkits,
  ]);

  // Combine reducer state with derived values and collection prop
  const state: UseToolkitPickerState = useMemo(
    () => ({
      ...reducerState,
      activeTab: effectiveTab,
      toolkitCollection,
      filteredToolkits,
      filteredTools,
      hasStarterToolkits,
      hiddenToolkitCount,
      hiddenToolCount,
    }),
    [
      reducerState,
      effectiveTab,
      toolkitCollection,
      filteredToolkits,
      filteredTools,
      hasStarterToolkits,
      hiddenToolkitCount,
      hiddenToolCount,
    ]
  );

  // Fire onSearchEmpty once per unique query when either list shows no results
  const lastFiredQueryRef = useRef<string | null>(null);
  useEffect(() => {
    const trimmed = searchQuery.toLowerCase().trim();
    if (!trimmed) {
      lastFiredQueryRef.current = null;
      return;
    }
    const isToolkitsEmpty =
      filteredToolkits.size === 0 &&
      !(hasStarterToolkits && hiddenToolkitCount > 0);
    const isToolsEmpty =
      filteredTools.size === 0 && !(hasStarterToolkits && hiddenToolCount > 0);
    if (
      isToolkitsEmpty &&
      isToolsEmpty &&
      lastFiredQueryRef.current !== trimmed
    ) {
      lastFiredQueryRef.current = trimmed;
      onSearchEmpty?.(searchQuery);
    }
  }, [
    filteredToolkits,
    filteredTools,
    searchQuery,
    hasStarterToolkits,
    hiddenToolkitCount,
    hiddenToolCount,
    onSearchEmpty,
  ]);

  // Update selections when initial props change
  useEffect(() => {
    if (
      toolkitCollection &&
      initialProps &&
      (initialProps.selectedToolkits ||
        initialProps.selectedTools ||
        initialProps.committedToolkits ||
        initialProps.committedTools)
    ) {
      dispatch({
        type: "SET_INITIAL_SELECTIONS",
        payload: initialProps,
        collection: toolkitCollection,
      });
    }
  }, [toolkitCollection, initialProps]);

  const contextValue: ToolkitPickerContextType = useMemo(
    () => ({
      state,
      toggleToolkit: (toolkit) =>
        dispatch({ type: "TOGGLE_TOOLKIT", payload: toolkit }),
      toggleTool: (tool) => {
        const toolFqn = tool.fully_qualified_name;
        const toolkitName = toolkitCollection?.toolToToolkitMap?.get(toolFqn);
        const toolkit = toolkitName
          ? toolkitCollection?.toolkitMap?.get(toolkitName)
          : undefined;
        dispatch({
          type: "TOGGLE_TOOL",
          payload: {
            toolFqn,
            toolkitName,
            toolkit,
            selectionMode: reducerState.selectionMode,
          },
        });
      },
      selectAll: (visibleToolFqns) =>
        dispatch({
          type: "SELECT_ALL",
          payload: { visibleToolFqns, collection: toolkitCollection },
        }),
      clearSelections: () => dispatch({ type: "CLEAR_SELECTIONS" }),
      commitSelections: () => dispatch({ type: "COMMIT_SELECTIONS" }),
      restoreCommittedState: () =>
        dispatch({ type: "RESTORE_COMMITTED_STATE" }),
      setSearchQuery: (query) =>
        dispatch({ type: "SET_SEARCH_QUERY", payload: query }),
      clearSearchQuery: () => dispatch({ type: "CLEAR_SEARCH_QUERY" }),
      setToolkitFilter: (fqName) =>
        dispatch({ type: "SET_TOOLKIT_FILTER", payload: fqName }),
      clearFilter: () => dispatch({ type: "CLEAR_FILTER" }),
      setActiveTab: (tab) => dispatch({ type: "SET_ACTIVE_TAB", payload: tab }),
      toggleUnoptimizedToolkits: () =>
        dispatch({ type: "TOGGLE_UNOPTIMIZED_TOOLKITS" }),
    }),
    [state, toolkitCollection, reducerState.selectionMode]
  );

  return (
    <ToolkitPickerContext.Provider value={contextValue}>
      {children}
    </ToolkitPickerContext.Provider>
  );
}

// Hook to use the context
export function useToolkitPicker() {
  const context = useContext(ToolkitPickerContext);
  if (!context) {
    throw new Error(
      "useToolkitPicker must be used within a ToolkitPickerProvider. Make sure to wrap your component with <ToolkitPickerProvider>."
    );
  }
  return context;
}

// Export the state type for components
export type { UseToolkitPickerState };
